import { useState } from 'react';
import type { User } from '../lib/mockData';

interface HeaderProps {
  isDarkMode: boolean;
  onToggleTheme: () => void;
  currentUser: User | null;
  onSignOut: () => void;
}

export function Header({ isDarkMode, onToggleTheme, currentUser, onSignOut }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <header className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-gray-800 dark:to-gray-900 text-white shadow-lg sticky top-0 z-50">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-white dark:bg-gray-700 rounded-full p-2">
              <span className="text-2xl">📚</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold">Open Class Ug</h1>
              <p className="text-sm text-blue-100 dark:text-gray-300">Digital Learning Platform</p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* Theme Toggle Button */}
            <button
              onClick={onToggleTheme}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
              aria-label="Toggle dark mode"
              title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDarkMode ? (
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              )}
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6">
              <button 
                onClick={() => scrollToSection('home')}
                className="hover:text-blue-200 dark:hover:text-gray-300 transition-colors font-medium"
              >
                Home
              </button>
              <button 
                onClick={() => scrollToSection('learning')}
                className="hover:text-blue-200 dark:hover:text-gray-300 transition-colors font-medium"
              >
                Learning Sections
              </button>
              <button 
                onClick={() => scrollToSection('about')}
                className="hover:text-blue-200 dark:hover:text-gray-300 transition-colors font-medium"
              >
                About
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className="hover:text-blue-200 dark:hover:text-gray-300 transition-colors font-medium"
              >
                Contact Us
              </button>
              
              {/* Sign Out Button - Only show when logged in */}
              {currentUser && (
                <button
                  onClick={onSignOut}
                  className="bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-500 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Sign Out
                </button>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden text-white focus:outline-none"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 space-y-2">
            <button 
              onClick={() => scrollToSection('home')}
              className="block w-full text-left py-2 hover:bg-blue-800 dark:hover:bg-gray-800 rounded px-2 transition-colors"
            >
              Home
            </button>
            <button 
              onClick={() => scrollToSection('learning')}
              className="block w-full text-left py-2 hover:bg-blue-800 dark:hover:bg-gray-800 rounded px-2 transition-colors"
            >
              Learning Sections
            </button>
            <button 
              onClick={() => scrollToSection('about')}
              className="block w-full text-left py-2 hover:bg-blue-800 dark:hover:bg-gray-800 rounded px-2 transition-colors"
            >
              About
            </button>
            <button 
              onClick={() => scrollToSection('contact')}
              className="block w-full text-left py-2 hover:bg-blue-800 dark:hover:bg-gray-800 rounded px-2 transition-colors"
            >
              Contact Us
            </button>
            
            {/* Sign Out Button - Mobile - Only show when logged in */}
            {currentUser && (
              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  onSignOut();
                }}
                className="block w-full text-left py-2 bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-500 rounded px-2 transition-colors font-medium"
              >
                Sign Out
              </button>
            )}
          </nav>
        )}
      </div>
    </header>
  );
}
